import React, { Fragment, useEffect, useState, useMemo } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Table, Button, Input, Label, Spinner, Modal, ModalBody } from 'reactstrap';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Breadcrumbs } from '../../AbstractElements';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../../Config/api';
import authHeader from './authHeader';

const today = () => new Date().toISOString().split('T')[0];
const SHIPPING_COST = 50;

// Normalise mixed-case catalog text (some titles are UPPERCASE, some Title Case) to a
// consistent Title Case for display.
const titleCase = (s) => (s || '')
  .toLowerCase()
  .replace(/(^|[\s\-/])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase());

// On-brand placeholder (a boutique dress in line-art) shown when a product has no photo.
const ProductPlaceholder = ({ size = 46 }) => (
  <div style={{
    width: size, height: size, borderRadius: 8,
    background: 'linear-gradient(135deg, #eef0fe 0%, #eef1f8 100%)',
    border: '1px solid var(--rf-border, #e7eaf3)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }} title="No image">
    <svg width={size * 0.62} height={size * 0.62} viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <path d="M17 12.5 L24 16 L31 12.5 L34.5 18.5 L29 21.5 L32 37 L16 37 L19 21.5 L13.5 18.5 Z"
        fill="#dcdcfb" stroke="#8b88e6" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M24 16 L24 21" stroke="#8b88e6" strokeWidth="1.2" strokeLinecap="round" opacity="0.7" />
    </svg>
  </div>
);

// Small inline thumbnail; falls back to the placeholder, click to enlarge.
const Thumb = ({ src, alt, onOpen }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return <ProductPlaceholder size={46} />;
  }
  return (
    <img src={src} alt={alt} loading="lazy" width={46} height={46}
      onError={() => setFailed(true)}
      onClick={onOpen}
      title="Click to enlarge"
      style={{ width: 46, height: 46, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--rf-border, #e7eaf3)', cursor: onOpen ? 'zoom-in' : 'default' }} />
  );
};

const PlaceOrder = () => {
  const { orderId } = useParams();
  const isEdit = !!orderId;
  const navigate = useNavigate();
  // Set when arriving from the dashboard's store picker.
  const [searchParams] = useSearchParams();
  const requestedStore = searchParams.get('store');

  const [stores, setStores] = useState([]);
  // A sign-in can cover several accounts (a chain keeps one per location). The
  // chosen store decides which account the order is billed to, so the header
  // context below follows the selection rather than a single fixed profile.
  const [accounts, setAccounts] = useState([]);
  const [products, setProducts] = useState([]); // full collection, each row carries s_qty/m_qty
  const [loading, setLoading] = useState(true);
  const [editStoreName, setEditStoreName] = useState(''); // store name shown read-only in edit mode

  const [storeId, setStoreId] = useState('');
  const [orderDate, setOrderDate] = useState(today());
  const [deliveryDate, setDeliveryDate] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [isImmediate, setIsImmediate] = useState(false);
  const [note, setNote] = useState('');
  const [filter, setFilter] = useState('');
  const [collectionFilter, setCollectionFilter] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { title, sku, images:[], index }

  const openLightbox = (it) => {
    const imgs = (it.images && it.images.length ? it.images : (it.image ? [it.image] : []));
    if (!imgs.length) return;
    setLightbox({ title: it.title, sku: it.sku, images: imgs, index: 0 });
  };
  const stepLightbox = (dir) => setLightbox((lb) => {
    if (!lb) return lb;
    const n = lb.images.length;
    return { ...lb, index: (lb.index + dir + n) % n };
  });

  useEffect(() => {
    (async () => {
      try {
        const [storesRes, profileRes, productsRes, curatedRes, detailRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/customer/stores`, authHeader()),
          axios.get(`${API_BASE_URL}/api/customer/profile`, authHeader()),
          axios.get(`${API_BASE_URL}/api/product-list`),
          axios.get(`${API_BASE_URL}/api/customer/order-products`, authHeader()),
          isEdit ? axios.get(`${API_BASE_URL}/api/customer/orders/${orderId}/detail`, authHeader()) : Promise.resolve(null),
        ]);
        const sList = storesRes.data.stores || [];
        setStores(sList);
        // /profile carries the billing address & VAT number per account; /stores
        // carries the shipping defaults. Merge them into one lookup by account.
        const billing = new Map((profileRes.data.profiles || []).map((p) => [p.id, p]));
        setAccounts((storesRes.data.accounts || []).map((a) => ({ ...a, ...(billing.get(a.id) || {}) })));

        // In edit mode, prefill order context + quantities from the existing order.
        let qtyMap = new Map();   // product_id -> { s_qty, m_qty }
        let pinned = new Set();   // product ids already in the order (sort to top)
        if (isEdit) {
          const d = detailRes.data;
          if (!d.order.editable) { toast.error('This order can no longer be edited.'); navigate('/customer/orders'); return; }
          setEditStoreName(d.order.store_name || '');
          setStoreId(String(d.order.store_id || ''));
          setCurrency(d.order.currency === 'USD' ? 'USD' : 'EUR');
          setOrderDate(d.order.order_date ? new Date(d.order.order_date).toISOString().split('T')[0] : today());
          setDeliveryDate(d.order.preferred_delivery_date ? new Date(d.order.preferred_delivery_date).toISOString().split('T')[0] : '');
          setIsImmediate(!!d.order.is_immediate);
          setNote(d.order.customer_note || '');
          (d.items || []).forEach((it) => {
            qtyMap.set(it.product_id, { s_qty: Number(it.s_qty) || 0, m_qty: Number(it.m_qty) || 0 });
            pinned.add(it.product_id);
          });
        } else {
          // Honour the store chosen on the dashboard; otherwise only auto-select
          // when there's no choice to make.
          const preselected = sList.find((s) => String(s.id) === String(requestedStore));
          if (preselected) setStoreId(String(preselected.id));
          else if (sList.length === 1) setStoreId(String(sList[0].id));
        }

        const mapped = (productsRes.data.products || []).map((p) => ({
          id: p.id,
          title: p.title || '',
          sku: p.sku || '',
          collection: p.collection || '',
          colour: p.colour || '',
          fabric: p.fabric || '',
          image: p.image || null,
          images: (p.images || []).map((i) => i.image_url).filter(Boolean),
          wholesale_price: parseFloat(p.wholesale_price || 0),
          wholesale_price_usd: parseFloat(p.wholesale_price_usd || 0),
          is_active: p.is_active == null ? 1 : Number(p.is_active),
          s_qty: qtyMap.get(p.id)?.s_qty || 0,
          m_qty: qtyMap.get(p.id)?.m_qty || 0,
        }));
        // Restrict to the products curated for the season order sheets (controlled
        // by admin in Settings → Order Settings). If nothing is curated yet, show
        // the full catalogue. In edit mode, always keep products already on the
        // order so it remains editable even if later removed from the sheet.
        let visible = mapped;
        if (curatedRes?.data?.curated) {
          const allowed = new Set((curatedRes.data.products || []).map((p) => p.id));
          visible = mapped.filter((p) => allowed.has(p.id) || pinned.has(p.id));
        }
        // Admin-disabled products aren't for sale — hide them from the customer's
        // add-to-order grid. Keep one already on the order visible so an edit
        // doesn't silently drop it, matching the curated-sheet rule above.
        visible = visible.filter((p) => p.is_active !== 0 || pinned.has(p.id));
        // Group similar products together: by title, then SKU.
        visible.sort((a, b) =>
          a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }) ||
          a.sku.localeCompare(b.sku, undefined, { sensitivity: 'base' }));
        // In edit mode, float the items already on the order to the top (keeping
        // their title/SKU order); the rest stay sorted as before.
        const ordered = isEdit
          ? [...visible.filter((p) => pinned.has(p.id)), ...visible.filter((p) => !pinned.has(p.id))]
          : visible;
        setProducts(ordered);
      } catch (e) {
        console.error('Failed to load order form', e);
        toast.error('Failed to load the order form');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, orderId, navigate, requestedStore]);

  const selectedStore = stores.find((s) => String(s.id) === String(storeId));
  // The account the selected store bills through — the one this order is filed
  // against, and whose VAT number and billing address appear on its invoice.
  const account = accounts.find((a) => a.id === selectedStore?.customer_id) || {};
  const multiAccount = accounts.length > 1;

  // Stores listed under their owning account, so a chain can tell two
  // similarly-named locations apart when choosing where to order for.
  const storeGroups = useMemo(() => {
    const byAccount = new Map();
    stores.forEach((s) => {
      if (!byAccount.has(s.customer_id)) byAccount.set(s.customer_id, []);
      byAccount.get(s.customer_id).push(s);
    });
    return [...byAccount.entries()].map(([id, list]) => ({
      id,
      name: accounts.find((a) => a.id === id)?.store_name || list[0]?.account_name || '',
      stores: list,
    }));
  }, [stores, accounts]);

  const sym = currency === 'USD' ? '$' : '€';
  const priceOf = (it) => (currency === 'USD' ? (it.wholesale_price_usd || it.wholesale_price) : it.wholesale_price);
  const rowQty = (it) => (Number(it.s_qty) || 0) + (Number(it.m_qty) || 0);
  const rowTotal = (it) => rowQty(it) * priceOf(it);

  const setQty = (id, field, val) => {
    setProducts((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: Math.max(0, parseInt(val || 0, 10)) } : it)));
  };

  // Header context shown read-only (managed under Billing Details / My Stores):
  // the store's own details, else those of the account it belongs to.
  const ctxEmail = (selectedStore && selectedStore.email) || account.email || '';
  const ctxPhone = (selectedStore && selectedStore.phone) || account.phone || account.tel || '';
  const ctxShipping = (selectedStore && selectedStore.shipping_address) || account.shipping_address || '';

  // Unique collection names for the dropdown (sorted, case-insensitive, de-duped).
  const collections = useMemo(() => {
    const map = new Map();
    products.forEach((p) => {
      const c = (p.collection || '').trim();
      if (c && !map.has(c.toLowerCase())) map.set(c.toLowerCase(), c);
    });
    return [...map.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [products]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const col = collectionFilter.trim().toLowerCase();
    return products.filter((p) => {
      if (col && (p.collection || '').toLowerCase() !== col) return false;
      if (!q) return true;
      return [p.title, p.sku, p.colour, p.fabric, p.collection].some((f) => (f || '').toLowerCase().includes(q));
    });
  }, [products, filter, collectionFilter]);

  const chosen = products.filter((it) => rowQty(it) > 0);
  const subtotal = chosen.reduce((a, it) => a + rowTotal(it), 0);
  const totalUnits = chosen.reduce((a, it) => a + rowQty(it), 0);

  const submit = async () => {
    if (!storeId) { toast.error('Please select a store'); return; }
    if (chosen.length === 0) { toast.error('Enter a quantity for at least one product'); return; }
    setSubmitting(true);
    try {
      const items = chosen.map((it) => ({ product_id: it.id, s_qty: it.s_qty, m_qty: it.m_qty }));
      if (isEdit) {
        await axios.put(`${API_BASE_URL}/api/customer/orders/${orderId}`,
          { items, preferred_delivery_date: deliveryDate || null, is_immediate: isImmediate, note }, authHeader());
        toast.success(`Order ${orderId} updated`);
        navigate('/customer/orders');
        return;
      }
      const res = await axios.post(`${API_BASE_URL}/api/customer/orders`, {
        store_id: Number(storeId),
        order_date: orderDate,
        preferred_delivery_date: deliveryDate || null,
        currency,
        items,
        is_immediate: isImmediate,
        note,
      }, authHeader());
      toast.success(`Order ${res.data.orderId} placed`);
      setProducts((prev) => prev.map((it) => ({ ...it, s_qty: 0, m_qty: 0 })));
      setDeliveryDate('');
      setFilter('');
      setIsImmediate(false);
      setNote('');
    } catch (e) {
      toast.error(e.response?.data?.error || (isEdit ? 'Failed to update order' : 'Failed to place order'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs parent="Dashboard" title={isEdit ? `Edit Order ${orderId}` : 'Place Order'} />
      <Container fluid={true}>
        {loading ? (
          <Card><CardBody><Spinner size="sm" /> <span className="text-muted">Loading the collection…</span></CardBody></Card>
        ) : (!isEdit && stores.length === 0) ? (
          <Card><CardBody>
            <p className="text-muted mb-0">You have no stores yet. Add one under <strong>My Stores</strong> before placing an order.</p>
          </CardBody></Card>
        ) : (
          <>
            {/* Order header */}
            <Card>
              <CardHeader className="pb-0"><h5>Order Details</h5></CardHeader>
              <CardBody>
                <Row>
                  <Col md="3">
                    <Label className="form-label">Store *</Label>
                    {isEdit ? (
                      <Input value={editStoreName} disabled className="mb-2" />
                    ) : (
                      <Input type="select" value={storeId} onChange={(e) => setStoreId(e.target.value)} className="mb-2">
                        <option value="">Select store…</option>
                        {multiAccount
                          ? storeGroups.map((g) => (
                            <optgroup key={g.id} label={g.name}>
                              {g.stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </optgroup>
                          ))
                          : stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </Input>
                    )}
                    {multiAccount && account.store_name && (
                      <p className="text-muted f-12 mb-2">Billed through <strong>{account.store_name}</strong></p>
                    )}
                  </Col>
                  <Col md="3">
                    <Label className="form-label">Order Date</Label>
                    <Input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} className="mb-2" disabled={isEdit} />
                  </Col>
                  <Col md="3">
                    <Label className="form-label">Preferred Delivery</Label>
                    <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="mb-2" />
                  </Col>
                  <Col md="3">
                    <Label className="form-label">Currency</Label>
                    <Input type="select" value={currency} onChange={(e) => setCurrency(e.target.value)} className="mb-2" disabled={isEdit}>
                      <option value="EUR">EUR (€)</option>
                      <option value="USD">USD ($)</option>
                    </Input>
                  </Col>
                </Row>
                <Row>
                  <Col md="4" className="d-flex align-items-center">
                    <Input type="checkbox" id="isImmediateOrder" checked={isImmediate}
                      onChange={(e) => setIsImmediate(e.target.checked)} className="me-2" style={{ marginTop: 0 }} />
                    <Label for="isImmediateOrder" className="form-label mb-0" style={{ cursor: 'pointer' }}>Mark as Immediate Order</Label>
                  </Col>
                  <Col md="8">
                    <Label className="form-label">Note for Admin (optional)</Label>
                    <Input type="textarea" rows="2" value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="Anything admin should know about this order…" className="mb-2" />
                  </Col>
                </Row>
                {storeId && (
                  <Row className="g-2 mt-1">
                    <Col md="4"><div className="text-muted f-12">Ship To</div><div style={{ whiteSpace: 'pre-wrap' }}>{ctxShipping || '—'}</div></Col>
                    <Col md="3"><div className="text-muted f-12">Billing</div><div style={{ whiteSpace: 'pre-wrap' }}>{account.billing_address || '—'}</div></Col>
                    <Col md="2"><div className="text-muted f-12">VAT / EORI</div><div>{account.vat_eori || '—'}</div></Col>
                    <Col md="3">
                      <div className="text-muted f-12">Contact</div>
                      <div>{ctxEmail || '—'}</div>
                      <div>{ctxPhone || ''}</div>
                    </Col>
                  </Row>
                )}
                <p className="text-muted f-12 mb-0 mt-2">Address &amp; contact details are managed under <strong>Billing Details</strong> and <strong>My Stores</strong>.</p>
              </CardBody>
            </Card>

            {/* Collection grid */}
            <Card>
              <CardHeader className="pb-0 d-flex justify-content-between align-items-center flex-wrap">
                <h5 className="mb-0">Collection</h5>
                <div className="d-flex gap-2 flex-wrap">
                  <div style={{ minWidth: 180 }}>
                    <Input type="select" bsSize="sm" value={collectionFilter} onChange={(e) => setCollectionFilter(e.target.value)}>
                      <option value="">All collections</option>
                      {collections.map((c) => <option key={c} value={c}>{c}</option>)}
                    </Input>
                  </div>
                  <div style={{ minWidth: 240 }}>
                    <Input bsSize="sm" value={filter} placeholder="Filter by title, SKU, colour, fabric…"
                      onChange={(e) => setFilter(e.target.value)} />
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <div className="table-responsive">
                  <Table className="align-middle" style={{ fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 56 }}></th>
                        <th>Collection</th>
                        <th>Title</th>
                        <th>Colour</th>
                        <th>Fabric</th>
                        <th>SKU</th>
                        <th className="text-end">Price</th>
                        <th style={{ width: 80 }} className="text-center">SM</th>
                        <th style={{ width: 80 }} className="text-center">ML</th>
                        <th className="text-center">Qty</th>
                        <th className="text-end">Line Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.length === 0 ? (
                        <tr><td colSpan="11" className="text-center text-muted py-3">No products match “{filter}”.</td></tr>
                      ) : visible.map((it) => {
                        const active = rowQty(it) > 0;
                        return (
                          <tr key={it.id} style={active ? { background: 'var(--rf-accent-soft, #eef0fe)' } : undefined}>
                            <td><Thumb src={it.image} alt={it.title} onOpen={() => openLightbox(it)} /></td>
                            <td>{it.collection || '-'}</td>
                            <td className="fw-semibold">{titleCase(it.title)}</td>
                            <td>{it.colour ? titleCase(it.colour) : '-'}</td>
                            <td>{it.fabric || '-'}</td>
                            <td className="text-muted">{it.sku}</td>
                            <td className="text-end">{sym}{priceOf(it).toFixed(2)}</td>
                            <td>
                              <Input bsSize="sm" type="number" min="0" className="text-center" value={it.s_qty}
                                onChange={(e) => setQty(it.id, 's_qty', e.target.value)} />
                            </td>
                            <td>
                              <Input bsSize="sm" type="number" min="0" className="text-center" value={it.m_qty}
                                onChange={(e) => setQty(it.id, 'm_qty', e.target.value)} />
                            </td>
                            <td className="text-center">{rowQty(it)}</td>
                            <td className="text-end">{sym}{rowTotal(it).toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </>
        )}
      </Container>

      {/* Sticky order summary bar */}
      {!loading && (isEdit || stores.length > 0) && (
        <div style={{
          position: 'sticky', bottom: 0, zIndex: 5,
          background: 'var(--rf-card, #fff)', borderTop: '1px solid var(--rf-border, #e7eaf3)',
          boxShadow: '0 -2px 10px rgba(20,28,56,.06)', padding: '12px 24px',
        }} className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex gap-4 flex-wrap">
            <span className="text-muted f-12">Items: <strong className="text-dark">{chosen.length}</strong> · Units: <strong className="text-dark">{totalUnits}</strong></span>
            <span className="text-muted f-12">Subtotal: <strong className="text-dark">{sym}{subtotal.toFixed(2)}</strong></span>
            <span className="text-muted f-12">Shipping: <strong className="text-dark">{sym}{SHIPPING_COST.toFixed(2)}</strong></span>
            <span>Total: <strong>{sym}{(subtotal + SHIPPING_COST).toFixed(2)}</strong></span>
          </div>
          <div className="d-flex gap-2">
            {isEdit && (
              <Button color="light" onClick={() => navigate('/customer/orders')} disabled={submitting}>Cancel</Button>
            )}
            <Button color="primary" onClick={submit} disabled={submitting || chosen.length === 0}>
              {submitting ? 'Saving…' : (isEdit ? 'Save Changes' : 'Place Order')}
            </Button>
          </div>
        </div>
      )}

      {/* Image lightbox */}
      <Modal isOpen={!!lightbox} toggle={() => setLightbox(null)} centered size="lg">
        <ModalBody className="p-0 position-relative" style={{ background: '#0f1117' }}>
          <Button close onClick={() => setLightbox(null)}
            style={{ position: 'absolute', top: 10, right: 14, zIndex: 2, filter: 'invert(1)' }} />
          {lightbox && (
            <div className="text-center">
              <img src={lightbox.images[lightbox.index]} alt={lightbox.title}
                style={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain' }} />
              {lightbox.images.length > 1 && (
                <>
                  <Button color="light" onClick={() => stepLightbox(-1)}
                    style={{ position: 'absolute', top: '50%', left: 10, transform: 'translateY(-50%)', opacity: 0.9 }}>‹</Button>
                  <Button color="light" onClick={() => stepLightbox(1)}
                    style={{ position: 'absolute', top: '50%', right: 10, transform: 'translateY(-50%)', opacity: 0.9 }}>›</Button>
                </>
              )}
              <div className="text-white d-flex justify-content-between align-items-center px-3 py-2" style={{ fontSize: 13 }}>
                <span><strong>{titleCase(lightbox.title)}</strong> <span className="text-muted">· {lightbox.sku}</span></span>
                {lightbox.images.length > 1 && <span className="text-muted">{lightbox.index + 1} / {lightbox.images.length}</span>}
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default PlaceOrder;
